/**
 * Mention rendering + token building.
 *
 * The backend parses three mention forms from a stored comment body. The picker
 * writes the first; the other two predate it and are still read so older
 * comments keep resolving:
 *
 *   1. <span data-mention-id="{userId}">@Display Name</span>   (current)
 *   2. @[{userId}]                                             (legacy)
 *   3. @{guid}                                                 (legacy)
 *
 * Form 1 keeps the display name in the markup, so a reader sees a name while the
 * machine-readable id stays in the body and survives export and re-parsing. The
 * legacy forms carry no name, so `renderMentions` rewrites them into a chip at
 * display time — otherwise every reader sees a raw GUID.
 */

const BRACKET_TOKEN = /@\[([^\]]+)\]/g;
const GUID_TOKEN = /@([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g;

/** Escapes a value for safe interpolation into an HTML attribute or text node. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The token the composer inserts for a picked user. Stored verbatim in the
 * comment body, so both the display name and the id must be escaped.
 */
export function buildMentionToken(userId: string, displayName: string): string {
  return `<span class="ccms-mention" data-mention-id="${escapeHtml(userId)}">@${escapeHtml(displayName)}</span>`;
}

/**
 * Rewrites legacy bare-id mention tokens into readable chips. Bodies already
 * using the span form pass through untouched.
 *
 * @param html      The stored comment body.
 * @param nameByIdOptional Display names keyed by user id, when known. A missing
 *                  name falls back to a generic label rather than the raw id.
 */
export function renderMentions(html: string, nameById?: Record<string, string>): string {
  if (!html) return '';

  const toChip = (userId: string) => {
    const name = nameById?.[userId] ?? nameById?.[userId.toLowerCase()];
    const label = name ? `@${name}` : '@mention';
    return `<span class="ccms-mention" data-mention-id="${escapeHtml(userId)}">${escapeHtml(label)}</span>`;
  };

  return html
    .replace(BRACKET_TOKEN, (_match, userId: string) => toChip(userId.trim()))
    .replace(GUID_TOKEN, (_match, userId: string) => toChip(userId));
}

/** The user IDs mentioned in a body, in any of the three supported forms. */
export function extractMentionIds(html: string): string[] {
  if (!html) return [];

  const ids = new Set<string>();
  const attribute = /data-mention-id\s*=\s*["']([^"']+)["']/gi;

  let match: RegExpExecArray | null;
  while ((match = attribute.exec(html)) !== null) ids.add(match[1].trim());

  // Reset lastIndex — these are module-level regexes with the global flag.
  BRACKET_TOKEN.lastIndex = 0;
  while ((match = BRACKET_TOKEN.exec(html)) !== null) ids.add(match[1].trim());

  GUID_TOKEN.lastIndex = 0;
  while ((match = GUID_TOKEN.exec(html)) !== null) ids.add(match[1]);

  return Array.from(ids).filter(Boolean);
}
